import { zodResolver } from "@hookform/resolvers/zod";
import {
  AudioLines,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Trash2,
} from "lucide-react";
import { FC, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { FileUploadInput } from "@/components/sku/file-upload-input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useUpdateSKUMutation } from "@/data/mutations";
import { MusicTabData } from "@/data/types";
import { useDialogState } from "@/hooks/use-dialog-state";
import { updatePlaylistTrack } from "@/lib/cms";

import { useCMSContext } from "../cms-context";
import { useCMSTabContext } from "../cms-tab-context";
import { useAudioTabContext } from "./audio-tab-context";

type AudioTrackProps = {
  track: MusicTabData;
  trackIndex: number;
};

const audioTrackFormSchema = z.object({
  track_url: z.string().url(),
  title: z.string().min(1),
  thumbnail_url: z.union([z.string().url(), z.literal("")]),
  artist: z.string(),
});

const AudioTrack: FC<AudioTrackProps> = ({ track, trackIndex }) => {
  const { tabIndex } = useCMSTabContext();

  const form = useForm<z.infer<typeof audioTrackFormSchema>>({
    resolver: zodResolver(audioTrackFormSchema),
    defaultValues: {
      track_url: track.options.track_url,
      title: track.options.title,
      thumbnail_url: track.options.thumbnail_url,
      artist: track.options.artist,
    },
  });
  const [isExpanded, setExpanded] = useState(false);
  const { sku, pageContent } = useCMSContext();
  const updateSKUMutation = useUpdateSKUMutation(sku.id);
  const { openDialog } = useDialogState();
  const { onDeletePlaylistTrack } = useAudioTabContext();

  const handleDelete = useCallback(() => {
    onDeletePlaylistTrack(trackIndex);
  }, [onDeletePlaylistTrack, trackIndex]);

  const onSubmit = (data: z.infer<typeof audioTrackFormSchema>) => {
    updateSKUMutation.mutate({
      page_content: updatePlaylistTrack(
        pageContent,
        tabIndex,
        trackIndex,
        data,
      ),
    });
  };

  return (
    <div className="rounded-lg overflow-hidden">
      <div className="bg-gray-100 flex p-3 justify-between">
        <div className="flex gap-2 items-center">
          <GripVertical className="size-4" />
          <div className="size-13 min-w-13 rounded-sm bg-[hsla(0,0%,0%,0.25)] flex items-center justify-center overflow-hidden">
            {track.options.thumbnail_url ? (
              <img
                src={track.options.thumbnail_url}
                alt="thumbnail"
                className="size-full object-cover"
              />
            ) : (
              <AudioLines className="text-white" />
            )}
          </div>
          <div>
            <p className="text-sm line-clamp-1 break-all">
              {track.options.title}
            </p>
            <p className="text-sm text-muted-foreground line-clamp-1 break-all">
              {track.options.artist}
            </p>
          </div>
        </div>

        <div className="flex items-center">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            onClick={() =>
              openDialog({
                name: "delete-playlist-track",
                params: { onDelete: handleDelete },
              })
            }
          >
            <Trash2 className="size-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setExpanded(!isExpanded)}
            type="button"
          >
            {isExpanded ? (
              <ChevronUp className="size-4" />
            ) : (
              <ChevronDown className="size-4" />
            )}
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div>
          <Form {...form}>
            <div className="bg-gray-50 flex flex-col gap-3 items-end p-4 xl:pl-32">
              <FormField
                name="track_url"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex w-full gap-4 items-center">
                    <FormLabel className="w-18">Audio file*</FormLabel>
                    <FormControl>
                      <Input {...field} className="bg-white flex-1" disabled />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                name="thumbnail_url"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex w-full gap-4 items-center">
                    <FormLabel className="w-18">Thumbnail</FormLabel>
                    <FormControl>
                      <FileUploadInput
                        {...field}
                        fileType="image"
                        className="bg-white flex-1"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                name="title"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex w-full gap-4 items-center">
                    <FormLabel className="w-18">Track Name*</FormLabel>
                    <div className="flex-1 space-y-1">
                      <FormControl>
                        <Input
                          {...field}
                          className="bg-white flex-1"
                          placeholder="Track Name"
                        />
                      </FormControl>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                name="artist"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex w-full gap-4 items-center">
                    <FormLabel className="w-18">Artist Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="bg-white flex-1"
                        placeholder="Artist Name"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <Button
                type="button"
                onClick={form.handleSubmit(onSubmit)}
                disabled={updateSKUMutation.isPending}
              >
                Save
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};

type AudioTracksProps = {
  tracks: MusicTabData[];
};

export const AudioTracks: FC<AudioTracksProps> = ({ tracks }) => (
  <div className="space-y-4">
    {tracks.map((track, index) => (
      <AudioTrack key={index} track={track} trackIndex={index} />
    ))}
  </div>
);
